'use client'

/**
 * batch-68 handtekening (FO-345 §3.1) - "Mijn handtekening": één keer tekenen,
 * opslaan en daarna hergebruiken. Gedeeld door de portal (Mijn gegevens) en de
 * backoffice (Mijn profiel).
 *
 *  - Tekenvak met basislijn en label; Wissen, Ongedaan maken (laatste streek),
 *    Opslaan. Op een smalle staande telefoon de hint "Draai je telefoon voor
 *    meer ruimte"; liggend gebruikt het vak de volle breedte.
 *  - Na opslaan: het voorbeeld, "Opgeslagen op dd-mm-jjjj", Vervangen en
 *    Verwijderen. Optioneel een paraaf (kleiner vak, zelfde werking).
 *  - Een leeg vak of een te simpele krabbel wordt geweigerd met uitleg (de
 *    server controleert hetzelfde na het bijsnijden).
 *  - Een getypte naam als handtekening bestaat hier bewust niet (FO-345 §11).
 *  - `alleenLezen` (tijdens "inloggen als"): bekijken, niet wijzigen.
 *
 * Geen data-fetch hier: de app geeft de gegevens en de acties mee (server
 * actions), zodat dit component in beide apps hetzelfde werkt.
 */
import { useEffect, useRef, useState } from 'react'
import { SignaturePad, type SignaturePadHandle } from '../signature-pad'
import { cn } from '../../lib/utils'

export type HandtekeningSoortUi = 'HANDTEKENING' | 'PARAAF'

export interface OpgeslagenHandtekeningWeergave {
  id: string
  soort: HandtekeningSoortUi
  opgeslagenOp: string
  voorbeeldUrl: string
}

export type HandtekeningActieUitkomst = { ok: true } | { ok: false; melding: string }

/** De aanroep zelf viel om (verbinding weg, server-action onbereikbaar). */
const VERBINDING_MISLUKT = 'De verbinding viel weg. Controleer je verbinding en probeer het opnieuw.'

/**
 * Voer een actie uit zonder ooit op "bezig" te blijven hangen (review I7): een
 * weggevallen verbinding wordt een melding; een doorverwijzing (geen uitkomst)
 * telt niet als fout.
 */
async function veiligUitvoeren(
  actie: () => Promise<HandtekeningActieUitkomst>,
): Promise<HandtekeningActieUitkomst | null> {
  try {
    return (await actie()) ?? null
  } catch {
    return { ok: false, melding: VERBINDING_MISLUKT }
  }
}

export interface MijnHandtekeningKaartProps {
  handtekening: OpgeslagenHandtekeningWeergave | null
  paraaf: OpgeslagenHandtekeningWeergave | null
  alleenLezen: boolean
  onOpslaan: (input: {
    soort: HandtekeningSoortUi
    dataUrl: string
    aantalStreken: number
  }) => Promise<HandtekeningActieUitkomst>
  onVerwijderen: (soort: HandtekeningSoortUi) => Promise<HandtekeningActieUitkomst>
  /** De gedeelde kwaliteitscheck (`beoordeelHandtekeningKwaliteit` uit @streep/types). */
  beoordeel: (input: { soort: HandtekeningSoortUi; aantalStreken: number; breedtePx: number }) => string | null
  className?: string
}

const DATUM = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/** Staand op een smal scherm? Dan de hint om te draaien. */
function useStaandSmal(): boolean {
  const [staand, setStaand] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(orientation: portrait) and (max-width: 640px)')
    const update = () => setStaand(mq.matches)
    update()
    mq.addEventListener?.('change', update)
    return () => mq.removeEventListener?.('change', update)
  }, [])
  return staand
}

function SoortBlok(props: {
  soort: HandtekeningSoortUi
  opgeslagen: OpgeslagenHandtekeningWeergave | null
  alleenLezen: boolean
  onOpslaan: MijnHandtekeningKaartProps['onOpslaan']
  onVerwijderen: MijnHandtekeningKaartProps['onVerwijderen']
  beoordeel: MijnHandtekeningKaartProps['beoordeel']
}) {
  const { soort, opgeslagen, alleenLezen } = props
  const padRef = useRef<SignaturePadHandle>(null)
  const [bewerken, setBewerken] = useState(false)
  const [leeg, setLeeg] = useState(true)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)
  const [bevestigVerwijderen, setBevestigVerwijderen] = useState(false)
  const staandSmal = useStaandSmal()
  const isParaaf = soort === 'PARAAF'
  const naam = isParaaf ? 'paraaf' : 'handtekening'
  const tekenen = bewerken || !opgeslagen

  async function opslaan() {
    const pad = padRef.current
    if (!pad) return
    setFout(null)
    const aantalStreken = pad.aantalStreken?.() ?? (pad.isEmpty() ? 0 : 2)
    const breedtePx = pad.inktBreedte?.() ?? 0
    const melding = props.beoordeel({ soort, aantalStreken, breedtePx })
    if (melding) {
      setFout(melding)
      return
    }
    setBezig(true)
    const res = await veiligUitvoeren(() => props.onOpslaan({ soort, dataUrl: pad.toDataURL(), aantalStreken }))
    setBezig(false)
    if (!res) return
    if (!res.ok) {
      setFout(res.melding)
      return
    }
    pad.clear()
    setBewerken(false)
  }

  async function verwijderen() {
    setFout(null)
    setBezig(true)
    const res = await veiligUitvoeren(() => props.onVerwijderen(soort))
    setBezig(false)
    setBevestigVerwijderen(false)
    if (res && !res.ok) setFout(res.melding)
  }

  return (
    <section aria-labelledby={`mijn-${naam}-kop`} className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 id={`mijn-${naam}-kop`} className="text-sm font-semibold text-streep-donker">
          {isParaaf ? 'Mijn paraaf (optioneel)' : 'Mijn handtekening'}
        </h3>
        {opgeslagen && !bewerken && (
          <span className="text-xs text-streep-text-muted">
            Opgeslagen op {DATUM.format(new Date(opgeslagen.opgeslagenOp))}
          </span>
        )}
      </div>

      {!tekenen && opgeslagen && (
        <div className="space-y-3">
          <div className="flex items-center justify-center rounded-md border border-streep-border bg-white p-3">
            {opgeslagen.voorbeeldUrl ? (
              /* eslint-disable-next-line @next/next/no-img-element -- kortlevende eigenaar-URL via de proxy */
              <img
                src={opgeslagen.voorbeeldUrl}
                alt={`Je opgeslagen ${naam}`}
                className={cn('max-w-full object-contain', isParaaf ? 'max-h-16' : 'max-h-28')}
              />
            ) : (
              <p className="text-sm text-streep-text-muted">
                Er is een {naam} opgeslagen. De afbeelding is alleen voor de medewerker zelf zichtbaar.
              </p>
            )}
          </div>
          {!alleenLezen && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setBewerken(true)
                  setFout(null)
                }}
                className="min-h-11 flex-1 rounded-md border border-streep-border bg-white px-3 text-sm font-medium text-streep-donker hover:bg-streep-surface-alt"
              >
                Vervangen
              </button>
              {bevestigVerwijderen ? (
                <div className="flex flex-1 flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 p-2">
                  <span className="text-xs text-red-800">
                    Je {naam} verwijderen? Al getekende documenten houden hun handtekening.
                  </span>
                  <button
                    type="button"
                    disabled={bezig}
                    onClick={verwijderen}
                    className="min-h-11 rounded-md bg-red-700 px-3 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Verwijderen
                  </button>
                  <button
                    type="button"
                    onClick={() => setBevestigVerwijderen(false)}
                    className="min-h-11 rounded-md px-3 text-sm text-streep-text-muted"
                  >
                    Annuleren
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setBevestigVerwijderen(true)}
                  className="min-h-11 flex-1 rounded-md border border-streep-border bg-white px-3 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  Verwijderen
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {tekenen && alleenLezen && (
        <p className="rounded-md bg-streep-surface-alt p-3 text-sm text-streep-text-muted">
          Er is geen {naam} opgeslagen. Tijdens &quot;inloggen als&quot; kun je geen {naam} zetten: dat doet
          de medewerker zelf.
        </p>
      )}

      {tekenen && !alleenLezen && (
        <div className="space-y-2">
          {staandSmal && !isParaaf && (
            <p className="text-xs text-streep-text-muted">Draai je telefoon voor meer ruimte.</p>
          )}
          <SignaturePad
            ref={padRef}
            height={isParaaf ? 110 : 180}
            basislijn
            label={isParaaf ? 'Teken hier je paraaf' : 'Teken hier je handtekening'}
            toonVoetbalk={false}
            disabled={bezig}
            ariaLabel={isParaaf ? 'Tekenvak voor je paraaf' : 'Tekenvak voor je handtekening'}
            onChange={(isLeeg) => {
              setLeeg(isLeeg)
              if (!isLeeg) setFout(null)
            }}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={bezig || leeg}
              onClick={() => padRef.current?.clear()}
              className="min-h-11 flex-1 rounded-md border border-streep-border bg-white px-3 text-sm font-medium text-streep-donker hover:bg-streep-surface-alt disabled:opacity-50"
            >
              Wissen
            </button>
            <button
              type="button"
              disabled={bezig || leeg}
              onClick={() => padRef.current?.undo?.()}
              className="min-h-11 flex-1 rounded-md border border-streep-border bg-white px-3 text-sm font-medium text-streep-donker hover:bg-streep-surface-alt disabled:opacity-50"
            >
              Ongedaan maken
            </button>
            <button
              type="button"
              disabled={bezig || leeg}
              onClick={opslaan}
              className="min-h-11 flex-1 rounded-md bg-streep-donker px-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {bezig ? 'Bezig...' : 'Opslaan'}
            </button>
          </div>
          {opgeslagen && (
            <button
              type="button"
              onClick={() => {
                setBewerken(false)
                setFout(null)
              }}
              className="text-xs text-streep-text-muted underline-offset-2 hover:underline"
            >
              Annuleren, mijn opgeslagen {naam} houden
            </button>
          )}
        </div>
      )}

      {fout && (
        <p role="alert" className="text-sm text-red-700">
          {fout}
        </p>
      )}
    </section>
  )
}

export function MijnHandtekeningKaart(props: MijnHandtekeningKaartProps) {
  return (
    <div className={cn('space-y-6 rounded-lg border border-streep-border bg-white p-4', props.className)}>
      <div>
        <h2 className="text-base font-semibold text-streep-donker">Mijn handtekening</h2>
        <p className="mt-1 text-sm text-streep-text-muted">
          Zet je handtekening één keer klaar, dan teken je documenten met één tik. Je handtekening
          wordt nooit vanzelf geplaatst: je kiest per document zelf of je tekent. Niemand anders kan
          je opgeslagen handtekening zien of gebruiken.
        </p>
      </div>
      <SoortBlok
        soort="HANDTEKENING"
        opgeslagen={props.handtekening}
        alleenLezen={props.alleenLezen}
        onOpslaan={props.onOpslaan}
        onVerwijderen={props.onVerwijderen}
        beoordeel={props.beoordeel}
      />
      <SoortBlok
        soort="PARAAF"
        opgeslagen={props.paraaf}
        alleenLezen={props.alleenLezen}
        onOpslaan={props.onOpslaan}
        onVerwijderen={props.onVerwijderen}
        beoordeel={props.beoordeel}
      />
    </div>
  )
}
